import { InvoiceSliderContainer } from "./InvoiceSliderContainer";
import { useForm, useFieldArray, UseFormRegister } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Submit } from "@/components/_Shared/Form/Buttons/Submit";
import { GenericForm, GenericFormBGContainer } from "@/components/GenericForm";
import { createBulkInvoices } from "@/api/invoices";
import { InvoiceProps } from "@/interfaces/invoices_types";
import { Button } from "@/components/_Shared/Button";
import { Divider } from "@/components/misc";
import { Alert } from "@/components/_Shared/Form/Buttons/Alert";
import { useEffect, useState } from "react";
import { SearchSelect } from "@/components/_Shared/Form/Inputs/SearchSelect";
import { useFormData } from "@/components/_Shared/Form/Hooks/useFormDataFetch";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";

export const InvoiceItemSchema = z.object({
    project_id: z.string(),
    user_email: z.string(),
    invoice_date: z.string().default(new Date().toISOString().split("T")[0]),
    payment_amount: z.number().min(1, "Payment amount must be greater than 0"),
    notes: z.string().min(1, "Notes must be at least 1 character").default(""),
})

const InvoiceFormSchema = z.object({
    invoices: z.array(InvoiceItemSchema),
})

type InvoiceFormValues = z.infer<typeof InvoiceFormSchema>

type InvoiceFormContainerProps = {
    project_id: string;
    user_email: string;
    project_on_submit?: () => void; // Optional as this container might be used without the project form
};

/**
 * Container component for managing invoice form state and submission
 * Handles form validation, field array management, and API submission
 * 
 * @param project_id - ID of the project the invoices belong to
 * @param user_email - Email of the user creating the invoices
 * @param project_on_submit - Function that updates the project (Used for the button that sends both the project and the invoices)
 */
export function InvoiceFormContainer({ project_id, user_email, project_on_submit }: InvoiceFormContainerProps) {
    const [sendBothOnSubmitSuccess, setSendBothOnSubmitSuccess] = useState(false)
    
    const { 
        register,
        handleSubmit,
        control,
        formState: { errors, isSubmitting, isSubmitSuccessful },
        reset,
        setError
    } = useForm<InvoiceFormValues>({
        resolver: zodResolver(InvoiceFormSchema),
    })

    const { fields, append, remove } = useFieldArray({
        control,
        name: "invoices",
    })

    const onSubmit = async (data: InvoiceFormValues) => {
        const invoice_data: InvoiceProps[] = data.invoices.map((invoice) => ({
            ...invoice,
            project_id,
            user_email,
            payment_status: "Pending",
            invoice_date: invoice.invoice_date,
        }))

        const response = await createBulkInvoices(invoice_data)

        if (response.operation != "COMPLETED") {
            setError("root", {message: "An error occurred while submitting the invoice form." })
        } else {
            reset()
        }
    }

    const bothOnSubmits = async () => {
        if (!project_on_submit) {
            console.error("Project on submit is not defined. Are you using this container without the project form?")
            return
        }

        try {
            project_on_submit()
            setSendBothOnSubmitSuccess(true)
        } catch (error) {
            setSendBothOnSubmitSuccess(false)
            setError("root", {message: "An error occurred while submitting the invoice form."})
            console.error(error)
        }
    }

    const onAddInvoice = () => {
        append({
            project_id,
            user_email,
            invoice_date: new Date().toISOString().split("T")[0],
            payment_amount: 100,
            notes: "",
        })
    }

    return (
        <GenericForm onSubmit={handleSubmit(onSubmit)} form_id={"invoice-form"} className="pt-2">
            <GenericFormBGContainer>
                {isSubmitSuccessful && 
                <Alert
                    title="Success"
                    description={`The invoice(s) have been submitted successfully. ${sendBothOnSubmitSuccess ? "The project has also been updated." : ""}`} 
                    redirects={
                        [
                            {label: "To Projects", value: "/projects"},
                            {label: "To Dashboard", value: "/main_menu"}
                    ]
                    } />}
                
                <h1 className="text-2xl font-bold">Create Invoices</h1>
                <Divider />

            {/* Hidden inputs for project_id and user_email */}
            {fields.map((field, index) => (
                <HiddenFields 
                    key={field.id} 
                    project_id={project_id} 
                    user_email={user_email} 
                    register={register} 
                    index={index} 
                    id={field.id}
                />
            ))}

            {/* Error messages */}
            {errors.invoices && (
                <p className="mt-1 text-sm text-red-500">{errors.invoices.message}</p>
            )}

            {errors.root && (
                <p className="mt-1 text-sm text-red-500">{errors.root.message}</p>
            )}

            <InvoiceSliderContainer 
                control={control}
                register={register} 
                fields={fields}
                remove={remove}
                errors={errors}
            />
            
            {isSubmitting ? 
                <div className="flex justify-center w-full mx-auto">
                    <Button className="opacity-50 cursor-not-allowed">Processing...</Button>
                </div> :
                <div className="flex justify-center gap-2 p-4 my-2 border rounded-lg shadow-lg bg-orange-100/60 border-orange-500/60">
                    <Button onClick={onAddInvoice}>Add Invoice</Button>
                    {fields.length > 0 && <Submit label="Submit Invoice(s)" isSubmitting={isSubmitting} />}
                    {(fields.length > 0 && project_on_submit) && <button className="button" onClick={() => bothOnSubmits()}>Submit Invoice(s) and Update Project</button>}
                </div>
            }

            </GenericFormBGContainer>
        </GenericForm>
    );
}

function HiddenFields({project_id, user_email, register, index, id}: {project_id: string, user_email: string, register: UseFormRegister<InvoiceFormValues>, index: number, id: string}) {
    return (
        <div key={id} className="hidden">
        <input
            type="hidden"
            {...register(`invoices.${index}.project_id`)}
            defaultValue={project_id}
        />
        <input
            type="hidden"
            {...register(`invoices.${index}.user_email`)}
            defaultValue={user_email}
        />
    </div>
    )
}

/**
 * This form is meant to create new invoices without having to go through the project form.
 * So it's basically it's own form, but it uses the InvoiceFormContainer above so it's similar to what you'd see in the project form.
 * Used on the CreateInvoiceForm component.
 */
export function CreateInvoiceForm() {
    const { projectManagers, projects, fetchAllDataProject } = useFormData()
    const navigate = useNavigate()
    const { user } = useAuth()

    const { control, watch } = useForm<{project_id: {value: string, label: string}, user_email: {value: string, label: string}}>({
        resolver: zodResolver(z.object({
            project_id: z.object({value: z.string(), label: z.string()}),
            user_email: z.object({value: z.string(), label: z.string()}),
        })),
        defaultValues: {
            project_id: {value: "", label: ""},
            user_email: {value: user?.email || "", label: user?.name || ""},
        }
    })

    const watchProjectId = watch("project_id")?.value
    const watchUserEmail = watch("user_email")?.value

    useEffect(() => {
        fetchAllDataProject()
    }, [fetchAllDataProject])

    return (
        <div>
            <GenericForm form_id="invoice-form" onSubmit={() => {}}>
                <GenericFormBGContainer>
                    <h1 className="text-2xl font-bold">Find Project and Manager</h1>

                    <Divider />

                    <SearchSelect label="Project" name="project_id" options={projects} control={control}/>
                    <SearchSelect label="Manager" name="user_email" options={projectManagers} control={control}/>

                </GenericFormBGContainer>
            </GenericForm>

            {watchProjectId && watchUserEmail && <InvoiceFormContainer project_id={watchProjectId} user_email={watchUserEmail}/>}

            <div className="flex justify-center gap-2 p-4 my-2 border rounded-lg shadow-lg bg-orange-100/60 border-orange-500/60 w-[99%] mx-auto ">  
                <Button className="" onClick={() => navigate(-1)}>Cancel</Button>
            </div>
        </div>
    )
}